package com.ijoin.data.api

import com.ijoin.data.api.ApiClient.addAuthToken
import com.ijoin.data.model.ChatMessage
import com.ijoin.data.model.ChatThread
import com.ijoin.data.model.ChatUserSummary
import com.ijoin.data.model.SendMessageRequest
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.prepareGet
import io.ktor.client.request.setBody
import io.ktor.client.request.url
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.HttpHeaders
import io.ktor.client.statement.bodyAsChannel
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.channels.awaitClose
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.callbackFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import io.ktor.utils.io.readUTF8Line
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json

class ChatApi {
    private val client = ApiClient.client
    private val baseUrl = "${ApiClient.getBaseUrl()}/api/chats"
    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
    }

    suspend fun getChats(): Result<List<ChatThread>> {
        return safeApiCall {
            client.get(baseUrl) {
                addAuthToken()
            }
        }.mapCatching { response -> response.body() }
    }

    suspend fun openChatWithUser(userId: String): Result<ChatThread> {
        return safeApiCall {
            client.post("$baseUrl/with/$userId") {
                addAuthToken()
            }
        }.mapCatching { response -> response.body() }
    }

    suspend fun getMessages(chatId: String): Result<List<ChatMessage>> {
        return safeApiCall {
            client.get("$baseUrl/$chatId/messages") {
                addAuthToken()
            }
        }.mapCatching { response -> response.body() }
    }

    suspend fun sendMessage(chatId: String, content: String): Result<ChatMessage> {
        return safeApiCall {
            client.post("$baseUrl/$chatId/messages") {
                addAuthToken()
                contentType(ContentType.Application.Json)
                setBody(SendMessageRequest(content = content))
            }
        }.mapCatching { response -> response.body() }
    }

    fun streamMessages(chatId: String): Flow<ChatMessage> = callbackFlow {
        val streamJob = launch {
            try {
                while (isActive) {
                    val result = runCatching {
                        ApiClient.executeWithTokenRefresh {
                            client.prepareGet("$baseUrl/$chatId/stream") {
                                addAuthToken()
                                headers.append(HttpHeaders.Accept, ContentType.Text.EventStream.toString())
                            }.execute { response ->
                                val channel = response.bodyAsChannel()
                                val payloadBuilder = StringBuilder()

                                suspend fun flushPayload() {
                                    if (payloadBuilder.isEmpty()) return
                                    val payload = payloadBuilder.toString()
                                    payloadBuilder.clear()
                                    runCatching {
                                        json.decodeFromString<ChatMessage>(payload)
                                    }.onSuccess { message ->
                                        trySend(message).isSuccess
                                    }
                                }

                                while (isActive) {
                                    val rawLine = channel.readUTF8Line() ?: break
                                    if (rawLine.startsWith("data:")) {
                                        payloadBuilder.append(rawLine.removePrefix("data:").trimStart())
                                    } else if (rawLine.isBlank()) {
                                        flushPayload()
                                    }
                                }
                                flushPayload()
                            }
                        }
                    }

                    val failure = result.exceptionOrNull()
                    if (failure is CancellationException) {
                        // Don't rethrow - just exit gracefully
                        break
                    }

                    if (isActive) {
                        delay(RECONNECT_DELAY_MS)
                    }
                }
            } catch (_: CancellationException) {
                // Silently handle cancellation - this is expected when leaving chat
            }
        }

        awaitClose {
            // Cancel the job without throwing
            streamJob.cancel()
        }
    }

    private companion object {
        private const val RECONNECT_DELAY_MS = 2_000L
    }

    suspend fun markThreadRead(chatId: String): Result<Unit> {
        return safeApiCall {
            client.post("$baseUrl/$chatId/read") {
                addAuthToken()
            }
        }.map { }
    }

    suspend fun searchUsers(query: String): Result<List<ChatUserSummary>> {
        if (query.isBlank()) {
            return Result.success(emptyList())
        }

        return safeApiCall {
            client.get("$baseUrl/users/search") {
                addAuthToken()
                url {
                    parameters.append("query", query)
                }
            }
        }.mapCatching { response -> response.body() }
    }
}
